import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Table, Spinner } from 'react-bootstrap';
import { useListTransitionsQuery } from '../../app/api';
import { brand, cardStyle, headStyle, EntPage, StageBadge } from './common';
import { parseDay, shortDate } from '../../components/TransitionDashboard';

/* Entrepreneur -> Transitions: every firm with a transition plan, the ones
 * still transitioning first. A firm gets a plan when it is marked
 * transitioning (on its Firms page, or when it is created); the plan is kept
 * after the firm goes live as the record of the transition. */

const STATUS_LABEL = { on_track: 'On track', attention: 'Needs attention', at_risk: 'At risk' };
const STATUS_COLOR = { on_track: brand.tealBlue, attention: '#b9770e', at_risk: '#c0581a' };

export default function Transitions() {
  const navigate = useNavigate();
  const { data: firms = [], isLoading } = useListTransitionsQuery(undefined, { refetchOnMountOrArgChange: true });
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <EntPage title="Transitions"
      subtitle="Firms working towards becoming Entrepreneur firms, and the dashboard each one sees until Transition Day.">
      <Card style={cardStyle}>
        <Card.Header style={headStyle}>Transition plans</Card.Header>
        <Card.Body className="p-0">
          {isLoading ? <div className="text-center p-4"><Spinner size="sm" /></div> : firms.length === 0 ? (
            <div style={{ padding: '2rem', textAlign: 'center', color: brand.charcoal }}>
              <i className="bi bi-signpost-split" style={{ fontSize: '1.75rem', opacity: 0.4, display: 'block', marginBottom: '0.5rem' }} />
              No transitions yet. Open a firm under Entrepreneur -&gt; Firms and choose &quot;Mark as transitioning&quot;,
              or tick &quot;Still transitioning&quot; when you create it.
            </div>
          ) : (
            <Table hover responsive className="mb-0" style={{ fontSize: '0.88rem' }}>
              <thead><tr style={{ color: brand.charcoal }}>
                <th style={{ padding: '0.6rem 0.9rem' }}>Firm</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Transition Day</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Status</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Members</th>
                <th style={{ padding: '0.6rem 0.9rem' }}>Last updated</th>
                <th style={{ padding: '0.6rem 0.9rem', width: 30 }} />
              </tr></thead>
              <tbody>
                {firms.map(f => {
                  const go = parseDay(f.transition_date);
                  const days = go ? Math.round((go - today) / 86400000) : null;
                  const live = f.stage !== 'transitioning';
                  return (
                    <tr key={f.firm_id} style={{ cursor: 'pointer', opacity: live ? 0.65 : 1 }}
                      onClick={() => navigate(`/entrepreneur/transitions/${f.firm_id}`)}>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', fontWeight: 700, color: brand.deepBlue }}>
                        {f.firm_name}
                        {live
                          ? <span style={{ marginLeft: 8, fontSize: '0.72rem', color: brand.green, fontWeight: 700 }}>Live</span>
                          : <StageBadge stage={f.stage} style={{ marginLeft: 8 }} />}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>
                        {go ? shortDate(go, new Date(0)) : 'Not set'}
                        {!live && days !== null && days >= 0 && <span style={{ marginLeft: 6, fontSize: '0.78rem' }}>({days} day{days === 1 ? '' : 's'})</span>}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: STATUS_COLOR[f.status], fontWeight: 600 }}>
                        {live ? '-' : STATUS_LABEL[f.status] || f.status}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal }}>{f.member_count}</td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', color: brand.charcoal, fontSize: '0.8rem' }}>
                        {f.updated ? new Date(f.updated).toLocaleDateString() : ''}
                        {f.updated_by && <div style={{ fontSize: '0.72rem' }}>{f.updated_by}</div>}
                      </td>
                      <td style={{ padding: '0.55rem 0.9rem', verticalAlign: 'middle', textAlign: 'right' }}>
                        <i className="bi bi-chevron-right" style={{ color: brand.tealBlue }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </EntPage>
  );
}
